import type React from 'react';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useReducer,
} from 'react';

type ModalState = {
  isModalOpen: boolean;
  isClosingModal: boolean;
  confirmBeforeClosing: boolean;
  cancelPaymentBeforeClosing: boolean;
  hasError: boolean;
};

type ModalContextProps = ModalState & {
  cancelCloseModal: () => void;
  closeModal: () => void;
  confirmCloseModal: () => void;
  cancelPayment: () => void;
  setError: () => void;
  dispatch: React.Dispatch<{
    type: ActionTypes;
    payload?: never;
  }>;
};

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

type ActionTypes =
  | 'CANCEL_PAYMENT'
  | 'ONLINE_PAYMENT_INITIATED'
  | 'BANK_TRANSFER_INITIATED'
  | 'CREDIT_CARD_CHARGE_SUCCESS'
  | 'CREDIT_CARD_CHARGE_ERROR'
  | 'RESET'
  | 'CANCEL_CLOSING'
  | 'CLOSING'
  | 'SET_ERROR';

type ModalProviderProps = {
  onClose?: () => void;
  onCancelAndClose?: () => void;
  confirmBeforeClosing?: boolean;
  cancelPaymentBeforeClosing?: boolean;
  hasError?: boolean;
};

function ModalProvider({
  children,
  onClose,
  onCancelAndClose,
  confirmBeforeClosing: initialConfirmBeforeClosing = true,
  cancelPaymentBeforeClosing: initialCancelPaymentBeforeClosing = true,
  hasError: initialHasError = false,
}: PropsWithChildren<ModalProviderProps>) {
  const initialState: ModalState = {
    isModalOpen: true,
    isClosingModal: false,
    confirmBeforeClosing: initialConfirmBeforeClosing,
    cancelPaymentBeforeClosing: initialCancelPaymentBeforeClosing,
    hasError: initialHasError,
  };

  const [state, dispatch] = useReducer(
    (state: ModalState, action: { type: ActionTypes; payload?: never }) => {
      switch (action.type) {
        case 'BANK_TRANSFER_INITIATED':
        case 'ONLINE_PAYMENT_INITIATED':
        case 'CREDIT_CARD_CHARGE_SUCCESS':
        case 'CREDIT_CARD_CHARGE_ERROR':
          return {
            ...state,
            confirmBeforeClosing: false,
            cancelPaymentBeforeClosing: false,
          };
        case 'CLOSING':
          return { ...state, isClosingModal: true };
        case 'CANCEL_PAYMENT':
          return {
            ...state,
            isClosingModal: true,
            confirmBeforeClosing: true,
            cancelPaymentBeforeClosing: true,
          };
        case 'CANCEL_CLOSING':
          return { ...state, isClosingModal: false };
        case 'SET_ERROR':
          return {
            ...state,
            hasError: true,
            confirmBeforeClosing: false,
            cancelPaymentBeforeClosing: false,
          };
        case 'RESET':
        default:
          return initialState;
      }
    },
    initialState,
  );

  const confirmClose = useCallback(() => {
    state.cancelPaymentBeforeClosing ? onCancelAndClose?.() : onClose?.();
  }, [onClose, onCancelAndClose, state.cancelPaymentBeforeClosing]);

  const cancelClose = useCallback(() => {
    dispatch({ type: 'CANCEL_CLOSING' });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want it to run only when the state changes
  const close = useCallback(() => {
    if (state.confirmBeforeClosing) {
      dispatch({ type: 'CLOSING' });

      return;
    }

    confirmClose();
  }, [dispatch, confirmClose, state.confirmBeforeClosing]);

  const cancel = useCallback(() => {
    dispatch({ type: 'CANCEL_PAYMENT' });
  }, []);

  const setError = useCallback(() => {
    dispatch({ type: 'SET_ERROR' });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      cancelCloseModal: cancelClose,
      closeModal: close,
      confirmCloseModal: confirmClose,
      cancelPayment: cancel,
      setError,
      dispatch,
    }),
    [cancelClose, close, confirmClose, cancel, setError, state],
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

export { ModalContext, ModalProvider };
